// src/components/pages/admin-dash/Admin-Dashboard.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../utils/AxiosInstance";

import { Toast } from "../../ui/Spinner";

import AdminSidebar    from "./AdminSidebar";
import AdminOverview   from "./AdminOverview";
import UserTable       from "./UserTable";
import AdminAccessList from "./AdminAccessList";
import UserModal       from "./UserModal";
import EditUserModal   from "./EditUserModal";
import PaymentsListByFilter from "./PaymentsListByFilter";
import PaymentsListByUser   from "./PaymentsListByUser";
import PaymentsUpdate       from "./PaymentsUpdate";
import DrawsListAll    from "./DrawsListAll";
import DrawGetById     from "./DrawGetById";
import DrawCreate      from "./DrawCreate";
import DrawEdit        from "./DrawEdit";
import ContactMessages from "./ContactMessages";
import BulkCredits     from "./BulkCredits";
import AdminScratchSources from "./AdminScratchSources";
import ScratchSettingsAdmin from "./ScratchSettingsAdmin";
import AdminSubscriptions  from "./AdminSubscriptions";
import AdminAuditLog       from "./AdminAuditLog";
import AdminAuditActivitySummary from "./AdminAuditActivitySummary";
import CacheAdmin          from "./CacheAdmin";
import LoggingAdmin        from "./LoggingAdmin";
import GlobalConfigurationAdmin from "./GlobalConfigurationAdmin";
import JobRunsAdmin        from "./JobRunsAdmin";
import AdminSystemHealth   from "./AdminSystemHealth";
import AdminRateLimit      from "./AdminRateLimit";
import AdminWebhookLog     from "./AdminWebhookLog";
import AdminMailingList    from "./AdminMailingList";
import AdminMatchResultStatsByParameter from "./AdminMatchResultStatsByParameter";
import AdminMatchResultTopPrizes        from "./AdminMatchResultTopPrizes";
import AdminMatchResultHitDistribution  from "./AdminMatchResultHitDistribution";
import AdminMatchResultExtraBallImpact  from "./AdminMatchResultExtraBallImpact";
import AdminFinancialReport             from "./AdminFinancialReport";
import AdminSubscriptionRetentionReport from "./AdminSubscriptionRetentionReport";
import AdminScratchStateReport          from "./AdminScratchStateReport";

/**
 * Admin dashboard root.
 *
 * Layout: full-bleed page (we deliberately do NOT wrap in AppShell so
 * the admin gets full width — the rest of the app is constrained to
 * 1180px which would feel cramped for tables).
 *
 * Sidebar (left) holds the section nav. Right pane swaps views based
 * on `activeView` state. Toast notifications are shared across all
 * views and surfaced via the `showToast` callback passed to children.
 *
 * View keys correspond to AdminSidebar items:
 *   overview · viewAllUsers · bulkCredits · auditLog
 *   subscriptions
 *   listPaymentsByFilter · listPaymentsByUser · updatePayment
 *   listAllDraws · getDrawById · createDraw · editDraw
 *   messages
 *   scratchSources · scratchIngest · scratchSettings
 *   cache · logging
 *   reportByParameter · reportTopPrizes · reportHitDistribution · reportExtraBallImpact
 *   reportFinancial · reportRetention · reportScratchByState
 *
 * onNavigate(view, params?)  is passed to sub-components so they can
 * jump to another section (e.g. Overview → updatePayment for a specific
 * payment ID). `params` are stashed in `viewParams` and only relevant
 * to the destination view.
 */
function AdminDashboard() {
  /* ----------------------------- Logged user -------------------------- */
  const userType = useSelector((state) => state.user.user?.type);
  const isSupport = userType === "SUPPORT";

  /* ----------------------------- View state -------------------------- */
  const [activeView, setActiveView]   = useState(isSupport ? "messages" : "overview");
  const [viewParams, setViewParams]   = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  /* ----------------------------- Users data -------------------------- */
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [searchTerm, setSearchTerm]     = useState("");
  const [filterType, setFilterType]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  /* ----------------------------- Toast ------------------------------- */
  const [toast, setToast] = useState({ open: false, tone: "success", message: "" });
  const showToast = useCallback((message, tone = "success") => {
    setToast({ open: true, tone, message });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 3500);
  }, []);

  /* ----------------------------- Navigation -------------------------- */
  const navigate = useCallback((view, params = {}) => {
    setActiveView(view);
    setViewParams(params);
  }, []);

  /* ----------------------------- Data fetchers ----------------------- */
  const fetchAllUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await axiosInstance.get("/admin/user/list-all");
      setAllUsers(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAllUsers([]);
      showToast("Failed to load users.", "error");
    } finally {
      setUsersLoading(false);
    }
  }, [showToast]);

  // Re-fetch users when the user table (or the admin/support subset) is shown
  useEffect(() => {
    if (activeView === "viewAllUsers" || activeView === "adminAccess") fetchAllUsers();
  }, [activeView, fetchAllUsers]);

  /* ----------------------------- User edit save ---------------------- */
  const handleEditSave = async (data) => {
    await axiosInstance.put(`/admin/user/update/${data.id}`, data);
    showToast("User updated.", "success");
    if (activeView === "viewAllUsers" || activeView === "adminAccess") fetchAllUsers();
  };

  /* ----------------------------- Impersonation ------------------------ */
  // Full page reload on success — the backend already swapped the session
  // cookies, so a fresh /site/user/logged-user on load picks up the
  // impersonated session. No point pre-seeding Redux first, the reload
  // wipes it anyway.
  const handleImpersonate = async (targetUser) => {
    try {
      await axiosInstance.post(`/admin/user/${targetUser.id}/impersonate`);
      window.location.href = "/";
    } catch {
      showToast("Failed to start impersonation.", "error");
    }
  };

  /* ----------------------------- Render view ------------------------- */
  const renderView = () => {
    // SUPPORT only has backend access to the contact-message endpoints — the
    // sidebar already hides every other item, this is just a defense so a
    // stale `activeView` (e.g. leftover viewParams navigation) can't strand
    // the UI on a view that will just 403.
    if (isSupport && activeView !== "messages") {
      return <ContactMessages onShowToast={showToast} />;
    }

    switch (activeView) {
      case "overview":
        return (
          <AdminOverview onNavigate={navigate} onShowToast={showToast} />
        );

      case "viewAllUsers":
        return (
          <UserTable
            users={allUsers}
            loading={usersLoading}
            onRefresh={fetchAllUsers}
            onEdit={setEditUser}
            onRowClick={setSelectedUser}
            onImpersonate={handleImpersonate}
            searchTerm={searchTerm}     setSearchTerm={setSearchTerm}
            filterType={filterType}     setFilterType={setFilterType}
            filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            focusUserId={viewParams.focusUserId}
          />
        );

      case "adminAccess":
        return (
          <AdminAccessList
            users={allUsers}
            loading={usersLoading}
            onRefresh={fetchAllUsers}
            onEdit={setEditUser}
            onRowClick={setSelectedUser}
            onImpersonate={handleImpersonate}
          />
        );

      case "bulkCredits":
        return <BulkCredits onShowToast={showToast} />;

      case "auditLog":
        return <AdminAuditLog />;

      case "auditActivitySummary":
        return <AdminAuditActivitySummary />;

      case "subscriptions":
        return <AdminSubscriptions onShowToast={showToast} />;

      case "listPaymentsByFilter":
        return <PaymentsListByFilter />;

      case "listPaymentsByUser":
        return <PaymentsListByUser />;

      case "updatePayment":
        return (
          <PaymentsUpdate
            initialPaymentId={viewParams.paymentId}
            onShowToast={showToast}
          />
        );

      case "reportByParameter":
        return <AdminMatchResultStatsByParameter />;

      case "reportTopPrizes":
        return <AdminMatchResultTopPrizes />;

      case "reportHitDistribution":
        return <AdminMatchResultHitDistribution />;

      case "reportExtraBallImpact":
        return <AdminMatchResultExtraBallImpact />;

      case "reportFinancial":
        return <AdminFinancialReport />;

      case "reportRetention":
        return <AdminSubscriptionRetentionReport />;

      case "reportScratchByState":
        return <AdminScratchStateReport />;

      case "listAllDraws":
        return <DrawsListAll onNavigate={navigate} onShowToast={showToast} />;

      case "getDrawById":
        return <DrawGetById onNavigate={navigate} onShowToast={showToast} />;

      case "createDraw":
        return (
          <DrawCreate
            onShowToast={showToast}
            onCreated={() => navigate("listAllDraws")}
          />
        );

      case "editDraw":
        return (
          <DrawEdit
            drawId={viewParams.drawId}
            onShowToast={showToast}
            onSaved={() => navigate("listAllDraws")}
            onDeleted={() => navigate("listAllDraws")}
          />
        );

      case "messages":
        return <ContactMessages onShowToast={showToast} />;

      case "mailingList":
        return <AdminMailingList />;

      case "scratchSources":
        return <AdminScratchSources mode="sources" onShowToast={showToast} />;

      case "scratchIngest":
        return <AdminScratchSources mode="ingest" onShowToast={showToast} />;

      case "scratchSettings":
        return <ScratchSettingsAdmin onShowToast={showToast} />;

      case "cache":
        return <CacheAdmin onShowToast={showToast} />;

      case "logging":
        return <LoggingAdmin onShowToast={showToast} />;

      case "globalConfiguration":
        return <GlobalConfigurationAdmin onShowToast={showToast} />;

      case "jobRuns":
        return <JobRunsAdmin onShowToast={showToast} />;

      case "systemHealth":
        return <AdminSystemHealth onNavigate={navigate} onShowToast={showToast} />;

      case "rateLimit":
        return <AdminRateLimit onShowToast={showToast} />;

      case "webhookLog":
        return <AdminWebhookLog />;

      default:
        return (
          <AdminOverview onNavigate={navigate} onShowToast={showToast} />
        );
    }
  };

  return (
    <div
      className="flex"
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        color: "var(--txt)",
      }}
    >
      <Toast open={toast.open} tone={toast.tone} message={toast.message} />

      <AdminSidebar
        activeView={activeView}
        onSelect={(id) => navigate(id)}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        userType={userType}
      />

      <main
        className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
        style={{ overflowX: "auto" }}
      >
        <div className="max-w-[1400px] mx-auto">{renderView()}</div>
      </main>

      {/* Modals — rendered at the root so they can open from any view */}
      {selectedUser && (
        <UserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onEdit={(u) => {
            setSelectedUser(null);
            setEditUser(u);
          }}
          onImpersonate={handleImpersonate}
        />
      )}

      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSave={async (data) => {
            await handleEditSave(data);
          }}
        />
      )}
    </div>
  );
}

export default AdminDashboard;